// Per-project custom field definition cache. Loaded when a project opens and
// refreshed after any mutation. Mirrors the structure of store/statuses.ts.

import { signal, type Signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import type { CustomFieldDef, CustomFieldDefCreate, CustomFieldDefUpdate } from "@juuplan/shared";
import {
  listCustomFields,
  createCustomField as apiCreateCustomField,
  updateCustomField as apiUpdateCustomField,
  reorderCustomFields as apiReorderCustomFields,
  deleteCustomField as apiDeleteCustomField,
} from "../lib/api";

export interface CustomFieldsState {
  loading: boolean;
  items: CustomFieldDef[];
  projectId: number | null;
  error: string | null;
}

const initial: CustomFieldsState = {
  loading: true,
  items: [],
  projectId: null,
  error: null,
};

const customFieldsSignal: Signal<CustomFieldsState> = signal(initial);
let inFlight: Promise<void> | null = null;

async function loadCustomFields(projectId: number): Promise<void> {
  try {
    const raw = await listCustomFields(projectId);
    const items = [...raw].sort((a, b) => a.sort_order - b.sort_order);
    customFieldsSignal.value = { loading: false, items, projectId, error: null };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Unknown error";
    customFieldsSignal.value = {
      loading: false,
      items: [],
      projectId,
      error: message,
    };
  } finally {
    inFlight = null;
  }
}

/**
 * Hook: subscribe to the custom field definitions for the given project.
 * Triggers a load when the projectId changes.
 */
export function useCustomFields(projectId: number): CustomFieldsState {
  useEffect(() => {
    if (!inFlight && customFieldsSignal.value.projectId !== projectId) {
      customFieldsSignal.value = {
        loading: true,
        items: [],
        projectId,
        error: null,
      };
      inFlight = loadCustomFields(projectId);
    }
  }, [projectId]);

  const state = customFieldsSignal.value;
  if (state.projectId !== projectId) {
    return { loading: true, items: [], projectId, error: null };
  }
  return state;
}

/** Force a fresh fetch — use after any schema-changing mutation. */
export function refreshCustomFields(projectId: number): Promise<void> {
  customFieldsSignal.value = { ...customFieldsSignal.value, loading: true };
  inFlight = loadCustomFields(projectId);
  return inFlight;
}

// ---------- CRUD helpers ----------

/** Create a new custom field def and refresh the cache. */
export async function addCustomField(
  projectId: number,
  body: CustomFieldDefCreate
): Promise<CustomFieldDef> {
  const created = await apiCreateCustomField(projectId, body);
  await refreshCustomFields(projectId);
  return created;
}

/** Update a custom field def and refresh the cache. */
export async function patchCustomField(
  id: number,
  projectId: number,
  body: CustomFieldDefUpdate
): Promise<CustomFieldDef> {
  const updated = await apiUpdateCustomField(id, body);
  await refreshCustomFields(projectId);
  return updated;
}

/** Reorder custom fields with optimistic update. */
export async function reorderCustomFieldDefs(
  projectId: number,
  orderedIds: number[]
): Promise<void> {
  const cur = customFieldsSignal.value;
  if (cur.projectId === projectId) {
    const byId = new Map(cur.items.map((f) => [f.id, f]));
    const reordered = orderedIds
      .map((id) => byId.get(id))
      .filter((f): f is CustomFieldDef => f !== undefined);
    customFieldsSignal.value = { ...cur, items: reordered };
  }
  try {
    await apiReorderCustomFields(projectId, orderedIds);
  } catch {
    await refreshCustomFields(projectId);
  }
}

/** Delete a custom field def and remove it from the cache. */
export async function removeCustomField(
  id: number,
  projectId: number
): Promise<void> {
  await apiDeleteCustomField(id);
  const cur = customFieldsSignal.value;
  if (cur.projectId === projectId) {
    customFieldsSignal.value = {
      ...cur,
      items: cur.items.filter((f) => f.id !== id),
    };
  }
}
